import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowUpRight, ArrowUp } from 'lucide-react';
import { useFooter, useContactInfo } from '../data';
import SocialRow from './SocialRow';
import IconChip from './IconChip';
import './Footer.css';

function FooterLink({ href, label }) {
  return href.includes('#') ? (
    <a href={href}>
      <span>{label}</span>
    </a>
  ) : (
    <Link to={href}>
      <span>{label}</span>
    </Link>
  );
}

const byOrder = (a, b) => (a.order || 0) - (b.order || 0);

export default function Footer() {
  const footer = useFooter();
  const contact = useContactInfo();
  const quickLinks = (footer.quickLinks || []).slice().sort(byOrder);
  const serviceLinks = (footer.serviceLinks || []).slice().sort(byOrder);

  const toTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="footer">
      <div className="footer__grid-bg" aria-hidden="true" />
      <div className="footer__glow" aria-hidden="true" />

      {/* Brand band */}
      <div className="container footer__brandband">
        <div className="footer__brand">
          <div className="footer__brand-head">
            <img className="footer__emblem" src="/assets/logo/logo-emblem.svg" alt="" />
            <h3 className="footer__brand-title">{footer.brandName}</h3>
          </div>
          <p className="footer__blurb">{footer.blurb}</p>
          <p className="footer__row">
            <MapPin size={16} className="footer__row-icon" />
            <span>{footer.address}</span>
          </p>
        </div>

        <a className="footer__cta" href="/#contact">
          <span className="footer__cta-sub">Have a project in mind?</span>
          <span className="footer__cta-main">
            Let's build it <ArrowUpRight size={20} />
          </span>
        </a>
      </div>

      <div className="container footer__cols">
        {/* Quick Links */}
        <div className="footer__col">
          <h4 className="footer__title">{footer.quickLinksTitle || 'Quick Links'}</h4>
          <ul className="footer__links">
            {quickLinks.map((l, i) => (
              <li key={`${l.label}-${i}`}>
                <FooterLink href={l.href} label={l.label} />
              </li>
            ))}
          </ul>
        </div>

        {/* Our Services */}
        <div className="footer__col">
          <h4 className="footer__title">{footer.servicesTitle || 'Our Services'}</h4>
          <ul className="footer__links">
            {serviceLinks.map((l, i) => (
              <li key={`${l.label}-${i}`}>
                <FooterLink href={l.href} label={l.label} />
              </li>
            ))}
          </ul>
        </div>

        {/* Connect */}
        <div className="footer__col footer__connect">
          <h4 className="footer__title">{footer.connectTitle || 'Connect With Us'}</h4>
          <a className="footer__contact" href={`tel:${contact.phone.replace(/\s+/g, '')}`}>
            <IconChip size={34} variant="accent">
              <Phone size={15} />
            </IconChip>
            <span>{contact.phone}</span>
          </a>
          <a className="footer__contact" href={`mailto:${contact.email}`}>
            <IconChip size={34} variant="accent">
              <Mail size={15} />
            </IconChip>
            <span>{contact.email}</span>
          </a>
          <SocialRow variant="chips" order={['linkedin', 'facebook']} size={18} />
        </div>
      </div>

      <div className="footer__bottom">
        <div className="container footer__bottom-inner">
          <p>© 2018 KamalCoding. All rights reserved.</p>
          <button className="footer__totop" onClick={toTop} aria-label="Back to top">
            Back to top <ArrowUp size={15} />
          </button>
        </div>
      </div>
    </footer>
  );
}
